import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Copy, Share2, Check, Sparkles, Lock, Users, Mail, TrendingUp, Award } from "lucide-react";

function useClipboard() {
    const [copied, setCopied] = useState(false);
    const timer = useRef(null);
    const copy = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            clearTimeout(timer.current);
            timer.current = setTimeout(() => setCopied(false), 2000);
        } catch {}
    };
    return { copied, copy };
}

function TierCard({ tier, signups, testid }) {
    const unlocked = tier.unlocked;
    const pct = Math.round(tier.progress * 100);
    return (
        <div className={`border p-5 relative overflow-hidden transition-colors ${unlocked ? "border-[var(--gold)] bg-[var(--surface-hover)]" : "border-[var(--border)] bg-[var(--surface)]"}`} data-testid={testid}>
            {unlocked && <div className="absolute top-0 right-0 bg-[var(--gold)] text-[#0a0908] px-3 py-1 font-mono text-[10px] tracking-[0.2em]">UNLOCKED</div>}
            <div className="flex items-center gap-3 mb-3">
                {unlocked ? <Award size={18} className="text-[var(--gold-bright)]" /> : <Lock size={16} className="text-[var(--text-muted)]" />}
                <p className="font-display text-xl tracking-[0.1em]">{tier.label}</p>
            </div>
            <p className="text-xs text-[var(--text-muted)] mb-4">
                Refer <strong className="text-[var(--text)]">{tier.threshold}</strong> coaches → unlock <strong className="text-[var(--gold-bright)] uppercase">{tier.tier}</strong> for {tier.days} days
            </p>
            <div className="h-1.5 bg-[var(--bg-deep)] relative overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[var(--gold-deep)] via-[var(--gold)] to-[var(--gold-bright)]" style={{ width: `${pct}%` }} />
            </div>
            <p className="font-mono text-xs text-[var(--text-muted)] mt-2">{signups} / {tier.threshold}</p>
        </div>
    );
}

export default function GrowthPage() {
    const [data, setData] = useState(null);
    const [emails, setEmails] = useState("");
    const [audience, setAudience] = useState("coach");
    const [msg, setMsg] = useState("Hey — I think you'd get a lot out of NDE Fitness. It's changed how I coach. Use my invite link below.");
    const [sending, setSending] = useState(false);
    const [sendResult, setSendResult] = useState(null);
    const [err, setErr] = useState("");
    const { copied: copiedLink, copy: copyLink } = useClipboard();
    const { copied: copiedCode, copy: copyCode } = useClipboard();

    const load = async () => {
        const { data } = await api.get("/growth/overview");
        setData(data);
    };
    useEffect(() => { load(); }, []);

    const submitInvite = async (e) => {
        e.preventDefault();
        setErr(""); setSendResult(null);
        const list = emails.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean);
        if (list.length === 0) { setErr("Add at least one email."); return; }
        setSending(true);
        try {
            const { data: res } = await api.post("/growth/invite", { emails: list, audience, message: msg });
            setSendResult(res);
            setEmails("");
            load();
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setSending(false); }
    };

    const sharePayload = useMemo(() => {
        if (!data) return null;
        return {
            title: "Coach with NDE Fitness",
            text: `I run my coaching on NDE Fitness — programs, AI insights, client calendars, all in one luxury workspace. Join me:`,
            url: data.referral_link,
        };
    }, [data]);

    const nativeShare = async () => {
        if (!sharePayload) return;
        if (navigator.share) {
            try { await navigator.share(sharePayload); } catch {}
        } else {
            copyLink(sharePayload.url);
        }
    };

    if (!data) return <div className="p-8 text-[var(--text-muted)]">Loading…</div>;

    return (
        <div className="p-8 space-y-6" data-testid="growth-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <p className="label-tiny mb-2">/ Referrals · Rewards · Reach</p>
                    <h1 className="font-serif text-6xl">Growth</h1>
                    <p className="text-sm text-[var(--text-muted)] mt-2">Grow NDE Fitness, unlock Pro &amp; Elite features as rewards.</p>
                </div>
                {(data.pro_active || data.elite_active) && (
                    <div className="flex gap-2">
                        {data.elite_active && <span className="tag-pill border-[var(--gold-bright)] text-[var(--gold-bright)]" data-testid="elite-active-badge">ELITE · ACTIVE</span>}
                        {data.pro_active && <span className="tag-pill border-[var(--gold)] text-[var(--gold)]" data-testid="pro-active-badge">PRO · ACTIVE</span>}
                    </div>
                )}
            </div>

            {/* Two referral links: athletes vs coaches */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Athlete join link */}
                <div className="border border-[var(--gold)] bg-[var(--surface)] p-6 relative overflow-hidden" data-testid="athlete-join-card">
                    <div className="absolute inset-0 bg-stripes opacity-25 pointer-events-none" />
                    <div className="relative">
                        <div className="flex items-center justify-between mb-3">
                            <p className="label-tiny text-[var(--gold-bright)]">/ For athletes</p>
                            <span className="tag-pill border-[var(--gold)] text-[var(--gold-bright)]">RECOMMENDED</span>
                        </div>
                        <h3 className="font-serif text-2xl mb-2">Athlete join link</h3>
                        <p className="text-xs text-[var(--text-muted)] mb-4">Send this to anyone you want to coach. They sign up, get added to your roster, and land in their athlete portal.</p>
                        <div className="border border-[var(--border-bright)] bg-[var(--bg-deep)] px-3 py-3 font-mono text-xs text-[var(--gold-bright)] break-all mb-3" data-testid="athlete-join-link">
                            {`${window.location.origin}/join/${data.referral_code}`}
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => copyLink(`${window.location.origin}/join/${data.referral_code}`)} className="btn-ghost flex-1 inline-flex items-center justify-center gap-2" data-testid="copy-athlete-link-btn">
                                {copiedLink ? <Check size={14} className="text-[var(--success)]" /> : <Copy size={14} />}
                                {copiedLink ? "Copied" : "Copy"}
                            </button>
                            <button onClick={async () => {
                                const url = `${window.location.origin}/join/${data.referral_code}`;
                                if (navigator.share) { try { await navigator.share({ title: "Train with me on NDE Fitness", text: "I run my coaching here — join me:", url }); } catch {} }
                                else copyLink(url);
                            }} className="btn-primary flex-1 inline-flex items-center justify-center gap-2"><Share2 size={14} /> Share</button>
                        </div>
                    </div>
                </div>

                {/* Coach referral link (existing) */}
                <div className="border border-[var(--gold-deep)] bg-[var(--surface)] p-6">
                    <p className="label-tiny mb-3">/ For other coaches</p>
                    <h3 className="font-serif text-2xl mb-2">Coach referral link</h3>
                    <p className="text-xs text-[var(--text-muted)] mb-4">Invite other coaches to NDE — every signup unlocks Pro/Elite reward tiers.</p>
                    <div className="border border-[var(--border-bright)] bg-[var(--bg-deep)] px-3 py-3 font-mono text-xs text-[var(--text-muted)] break-all mb-3" data-testid="referral-link">
                        {data.referral_link}
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => copyLink(data.referral_link)} className="btn-ghost flex-1 inline-flex items-center justify-center gap-2" data-testid="copy-link-btn">
                            <Copy size={14} /> Copy
                        </button>
                        <button onClick={nativeShare} className="btn-ghost flex-1 inline-flex items-center justify-center gap-2" data-testid="share-link-btn">
                            <Share2 size={14} /> Share
                        </button>
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-3 text-sm">
                <span className="label-tiny">Your code</span>
                <button onClick={() => copyCode(data.referral_code)} className="font-mono text-[var(--gold-bright)] hover:text-[var(--gold)]" data-testid="copy-code-btn">
                    {data.referral_code} {copiedCode ? "✓" : "·  tap to copy"}
                </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--border)]">
                <div className="bg-[var(--surface)] p-6" data-testid="stat-invites-sent">
                    <div className="flex items-center justify-between mb-3">
                        <p className="label-tiny">Invites sent</p>
                        <Mail size={16} className="text-[var(--gold)]" />
                    </div>
                    <p className="font-display text-6xl">{data.invites_sent}</p>
                </div>
                <div className="bg-[var(--surface)] p-6" data-testid="stat-signups">
                    <div className="flex items-center justify-between mb-3">
                        <p className="label-tiny">Signups generated</p>
                        <Users size={16} className="text-[var(--gold)]" />
                    </div>
                    <p className="font-display text-6xl">{data.signups}</p>
                </div>
                <div className="bg-[var(--surface)] p-6" data-testid="stat-active">
                    <div className="flex items-center justify-between mb-3">
                        <p className="label-tiny">Active (14d)</p>
                        <TrendingUp size={16} className="text-[var(--gold-bright)]" />
                    </div>
                    <p className="font-display text-6xl">{data.active_referrals}</p>
                </div>
            </div>

            {/* Reward Tiers */}
            <div>
                <p className="label-tiny mb-3">/ Rewards</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {data.tiers.map((t, i) => <TierCard key={i} tier={t} signups={data.signups} testid={`tier-${t.threshold}`} />)}
                </div>
                {data.next_tier && (
                    <p className="text-sm text-[var(--text-muted)] mt-4">
                        <Sparkles size={12} className="inline text-[var(--gold)] mr-1" />
                        <strong className="text-[var(--text)]">{data.next_tier.threshold - data.signups}</strong> more signup{data.next_tier.threshold - data.signups === 1 ? "" : "s"} until you unlock <strong className="text-[var(--gold-bright)]">{data.next_tier.label}</strong>.
                    </p>
                )}
            </div>

            {/* Invite box */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <form onSubmit={submitInvite} className="border border-[var(--border)] bg-[var(--surface)] p-6 space-y-4" data-testid="invite-form">
                    <div>
                        <p className="label-tiny mb-1">/ Invite by email</p>
                        <h3 className="font-serif text-2xl">Send invites</h3>
                    </div>
                    <div>
                        <label className="label-tiny block mb-2">Audience</label>
                        <div className="flex gap-2">
                            {["coach", "client"].map((a) => (
                                <button key={a} type="button" onClick={() => setAudience(a)} className={`btn-ghost ${audience === a ? "border-[var(--gold)] text-[var(--gold-bright)]" : ""}`} data-testid={`audience-${a}`}>
                                    Invite {a}s
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="label-tiny block mb-2">Emails (comma / space separated)</label>
                        <textarea className="input-dark min-h-[80px]" placeholder="lena@gym.com, dan@club.io, …" value={emails} onChange={(e) => setEmails(e.target.value)} data-testid="invite-emails-input" />
                    </div>
                    <div>
                        <label className="label-tiny block mb-2">Personal note</label>
                        <textarea className="input-dark min-h-[70px]" value={msg} onChange={(e) => setMsg(e.target.value)} />
                    </div>
                    {err && <div className="text-sm text-[var(--danger)]" data-testid="invite-error">{err}</div>}
                    {sendResult && <div className="text-sm text-[var(--success)]" data-testid="invite-success">Logged {sendResult.created} invite{sendResult.created === 1 ? "" : "s"}. They'll count toward rewards once they sign up through your link.</div>}
                    <button type="submit" disabled={sending} className="btn-primary inline-flex items-center gap-2" data-testid="send-invites-btn">
                        <Mail size={14} /> {sending ? "Logging…" : "Log invites"}
                    </button>
                    <p className="text-[10px] text-[var(--text-faint)] font-mono">
                        * Email delivery uses your own client / device share sheet or the Share button above. We track signups via your referral link.
                    </p>
                </form>

                <div className="border border-[var(--border)] bg-[var(--surface)] p-6 space-y-4">
                    <div>
                        <p className="label-tiny mb-1">/ Recent</p>
                        <h3 className="font-serif text-2xl">Invite log</h3>
                    </div>
                    {data.recent_invites.length === 0 ? (
                        <p className="text-sm text-[var(--text-muted)]">No invites yet. Start with 3 to unlock your first reward.</p>
                    ) : (
                        <div className="divide-y divide-[var(--border)]">
                            {data.recent_invites.map((i) => (
                                <div key={i.id} className="py-3 flex items-center justify-between text-sm" data-testid={`invite-row-${i.id}`}>
                                    <span className="font-mono text-xs">{i.invited_email}</span>
                                    <span className={`tag-pill ${i.status === "signed_up" ? "border-[var(--gold-bright)] text-[var(--gold-bright)]" : ""}`}>{i.status.replace("_", " ")}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="text-center pt-4">
                <p className="text-xs text-[var(--text-muted)]">
                    Know another coach who'd love this? <Link to="/apply-coach" className="text-[var(--gold)]">Send them our apply page →</Link>
                </p>
            </div>
        </div>
    );
}
